import React, { useState, useEffect, useMemo } from 'react';
import { istDay } from '../utils/dates';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { leadService, userService, brandService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Spinner } from '../components/Spinner';
import toast from 'react-hot-toast';

// Forward-only status order
const STATUS_ORDER = ['new', 'in_progress', 'follow_up'];
const STATUS_LABELS = {
  new: 'New',
  in_progress: 'In Progress',
  follow_up: 'Follow Up',
  converted: 'Converted',
  lost: 'Lost',
};

const ROLE_LABELS = { admin: 'Admin', dealer: 'Dealer', sales_executive: 'Sales Executive' };

export const EditLead = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const queryClient = useQueryClient();

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin      = user?.role === 'admin';
  const isDealer     = user?.role === 'dealer';
  const isSalesExec  = user?.role === 'sales_executive';
  const userRole     = user?.role;

  // Fetch the existing lead
  const { data: leadData, isLoading: leadLoading } = useQuery(
    ['lead', id],
    () => leadService.getLead(id),
    { enabled: !!id }
  );

  const brandsQuery = useQuery('brands', brandService.getBrands, { staleTime: 5 * 60_000 });
  const brands = brandsQuery.data?.data?.data?.brands || [];

  const assignableUsersQuery = useQuery(
    ['assignableUsers', user?.role],
    () => userService.getUsers({ status: 'active', limit: 200 }),
    { enabled: hasPermission('leads', 'update') }
  );

  const allUsers = (() => {
    const d = assignableUsersQuery.data?.data;
    if (!d) return [];
    return d.users || d.data?.users || [];
  })();

  const lead = leadData?.data?.data?.lead || leadData?.data?.lead;

  // Form state — populated once lead loads
  const [formData, setFormData] = useState(null);

  // Populate form data when lead arrives (runs once)
  useEffect(() => {
    if (!lead || formData) return;

    setFormData({
      customerInfo: {
        name:           lead.customerInfo?.name || '',
        phone:          lead.customerInfo?.phone || '',
        alternatePhone: lead.customerInfo?.alternatePhone || '',
        email:          lead.customerInfo?.email || '',
        city:           lead.customerInfo?.city || '',
        subcity:        lead.customerInfo?.subcity || '',
        pincode:        lead.customerInfo?.pincode || '',
        address:        lead.customerInfo?.address || '',
      },
      leadInfo: {
        enquiryDate: lead.leadInfo?.enquiryDate
          ? istDay(lead.leadInfo.enquiryDate)   // the India day it came in
          : '',
        brand:         lead.leadInfo?.brand || '',
        model:         lead.leadInfo?.model || '',
        source:        lead.leadInfo?.source || 'phone',
        buyingTime:    lead.leadInfo?.buyingTime || 'just_enquiring',
        buyingTimeRaw: lead.leadInfo?.buyingTimeRaw || '',
      },
      dealerInfo: {
        dealerName:       lead.dealerInfo?.dealerName || '',
        dealerCity:       lead.dealerInfo?.dealerCity || '',
        dealerNumber:     lead.dealerInfo?.dealerNumber || '',
        dealerEmail:      lead.dealerInfo?.dealerEmail || '',
        dealershipStatus: lead.dealerInfo?.dealershipStatus || 'active',
      },
      status:   lead.status || 'new',
      priority: lead.priority || 'medium',
      notes:    lead.notes || '',
    });
  }, [lead, formData]);

  // Forward-only status options: all statuses from current onwards (including current)
  const allowedStatuses = useMemo(() => {
    if (!lead) return [];
    const currentIdx = STATUS_ORDER.indexOf(lead.status);
    if (currentIdx === -1) return []; // converted/lost — no editable statuses
    return STATUS_ORDER.slice(currentIdx); // includes current so user sees their current state
  }, [lead]);

  const selectedBrand   = brands.find(b => b.name === formData?.leadInfo?.brand);
  const availableModels = selectedBrand?.models?.filter(m => m.isActive) || [];

  // A brand/model imported from an external source (e.g. Facebook) may not
  // match anything in the catalog — surface it as a selectable custom entry
  // instead of letting the dropdown silently show nothing.
  const brandOptions = (formData?.leadInfo?.brand && !brands.some(b => b.name === formData.leadInfo.brand))
    ? [...brands, { _id: '__custom_brand__', name: formData.leadInfo.brand, custom: true }]
    : brands;
  const modelOptions = (formData?.leadInfo?.model && !availableModels.some(m => m.name === formData.leadInfo.model))
    ? [...availableModels, { _id: '__custom_model__', name: formData.leadInfo.model, custom: true }]
    : availableModels;

  // Current assignment info
  const assignedUser = lead?.assignment?.assignedTo;
  const assignedUserName = assignedUser
    ? `${assignedUser.firstName || ''} ${assignedUser.lastName || ''}`.trim()
    : 'Unassigned';
  const assignedRole = lead?.assignment?.assignedRole || assignedUser?.role || '';

  const updateLeadMutation = useMutation(
    (data) => leadService.updateLead(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['lead', id]);
        queryClient.invalidateQueries('leads');
        queryClient.invalidateQueries('leadTabStats');   // the tab counts, with the list
        toast.success('Lead updated successfully');
        navigate(`/leads?expanded=${id}`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update lead.');
      },
    }
  );

  const handleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasPermission('leads', 'update')) {
      toast.error('You do not have permission to update leads.');
      return;
    }

    if (!formData.customerInfo.name || !formData.customerInfo.phone) {
      toast.error('Customer name and mobile number are required.');
      return;
    }

    // Flatten nested objects for the backend's allowed fields format
    const payload = {};
    Object.entries(formData.customerInfo).forEach(([k, v]) => { payload[`customerInfo.${k}`] = v; });
    Object.entries(formData.leadInfo).forEach(([k, v]) => { payload[`leadInfo.${k}`] = v; });
    Object.entries(formData.dealerInfo).forEach(([k, v]) => { payload[`dealerInfo.${k}`] = v; });
    payload.priority = formData.priority;
    payload.notes = formData.notes;
    if (formData.status !== lead.status) {
      payload.status = formData.status;
    }

    updateLeadMutation.mutate(payload);
  };

  if (isSalesExec) {
    navigate('/leads', { replace: true });
    return null;
  }

  if (leadLoading || !formData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const inputCls = 'input text-sm';
  const labelCls = 'block text-xs text-gray-500 mb-1';

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/leads?expanded=${id}`)} className="p-2 text-gray-400 hover:text-gray-600 flex-shrink-0">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Edit Lead</h1>
            <p className="mt-0.5 text-sm text-gray-500">Update lead information</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-8">

          {/* ── Customer Information ── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100">
              Customer Information
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Customer Name <span className="text-red-500">*</span></label>
                <input
                  type="text" required
                  value={formData.customerInfo.name}
                  onChange={e => handleChange('customerInfo', 'name', e.target.value)}
                  className={inputCls} placeholder="Enter customer name"
                />
              </div>
              <div>
                <label className={labelCls}>Mobile Number <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  value={formData.customerInfo.phone}
                  readOnly
                  className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`}
                />
              </div>
              <div>
                <label className={labelCls}>Alternate Number</label>
                <input
                  type="tel"
                  value={formData.customerInfo.alternatePhone}
                  onChange={e => handleChange('customerInfo', 'alternatePhone', e.target.value)}
                  className={inputCls} placeholder="Enter alternate number"
                />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input
                  type="email"
                  value={formData.customerInfo.email}
                  onChange={e => handleChange('customerInfo', 'email', e.target.value)}
                  className={inputCls} placeholder="Enter email address"
                />
              </div>
              <div>
                <label className={labelCls}>City</label>
                <input
                  type="text"
                  value={formData.customerInfo.city}
                  onChange={e => handleChange('customerInfo', 'city', e.target.value)}
                  className={inputCls} placeholder="Enter city"
                />
              </div>
              <div>
                <label className={labelCls}>Subcity / Area</label>
                <input
                  type="text"
                  value={formData.customerInfo.subcity}
                  onChange={e => handleChange('customerInfo', 'subcity', e.target.value)}
                  className={inputCls} placeholder="Enter subcity or area (optional)"
                />
              </div>
              <div>
                <label className={labelCls}>Pincode</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={formData.customerInfo.pincode}
                  onChange={e => handleChange('customerInfo', 'pincode', e.target.value.replace(/\D/g, ''))}
                  className={inputCls} placeholder="6-digit pincode (optional)"
                />
              </div>
              <div>
                <label className={labelCls}>Address</label>
                <input
                  type="text"
                  value={formData.customerInfo.address}
                  onChange={e => handleChange('customerInfo', 'address', e.target.value)}
                  className={inputCls} placeholder="Enter address"
                />
              </div>
            </div>
          </div>

          {/* ── Lead Information ── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100">
              Lead Information
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Enquiry Date</label>
                <input
                  type="date"
                  value={formData.leadInfo.enquiryDate}
                  onChange={e => handleChange('leadInfo', 'enquiryDate', e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Brand Name</label>
                <select
                  value={formData.leadInfo.brand}
                  onChange={e => {
                    handleChange('leadInfo', 'brand', e.target.value);
                    handleChange('leadInfo', 'model', '');
                  }}
                  className={inputCls}
                >
                  <option value="">
                    {brandsQuery.isLoading ? 'Loading...' : 'Select Brand'}
                  </option>
                  {brandOptions.map(b => (
                    <option key={b._id} value={b.name}>{b.name}{b.custom ? ' (custom)' : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Model</label>
                <select
                  value={formData.leadInfo.model}
                  onChange={e => handleChange('leadInfo', 'model', e.target.value)}
                  className={inputCls}
                  disabled={!formData.leadInfo.brand || modelOptions.length === 0}
                >
                  <option value="">
                    {!formData.leadInfo.brand
                      ? 'Select brand first'
                      : modelOptions.length === 0
                        ? 'No models available'
                        : 'Select Model'}
                  </option>
                  {modelOptions.map(m => (
                    <option key={m._id} value={m.name}>{m.name}{m.custom ? ' (custom)' : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Buying Time</label>
                <select
                  value={formData.leadInfo.buyingTime}
                  onChange={e => handleChange('leadInfo', 'buyingTime', e.target.value)}
                  className={inputCls}
                >
                  <option value="just_enquiring">Just Enquiring</option>
                  <option value="immediate">Immediate</option>
                  <option value="within_1_month">Within 1 Month</option>
                  <option value="within_3_months">Within 3 Months</option>
                  <option value="within_6_months">Within 6 Months</option>
                  <option value="other">Other (custom)</option>
                </select>
                {formData.leadInfo.buyingTime === 'other' && (
                  <input
                    type="text"
                    value={formData.leadInfo.buyingTimeRaw}
                    onChange={e => handleChange('leadInfo', 'buyingTimeRaw', e.target.value)}
                    className={`${inputCls} mt-2`}
                    placeholder="Customer's original answer"
                  />
                )}
              </div>
              <div>
                <label className={labelCls}>Priority</label>
                <select
                  value={formData.priority}
                  onChange={e => handleFieldChange('priority', e.target.value)}
                  className={inputCls}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Notes</label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={e => handleFieldChange('notes', e.target.value)}
                  className={inputCls} placeholder="Add notes"
                />
              </div>
            </div>
          </div>

          {/* ── Status (Forward-Only) ── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100">
              Lead Status
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Status</label>
                {allowedStatuses.length > 0 ? (
                  <select
                    value={formData.status}
                    onChange={e => handleFieldChange('status', e.target.value)}
                    className={inputCls}
                  >
                    {allowedStatuses.map(s => (
                      <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text" readOnly
                    value={STATUS_LABELS[lead.status] || lead.status}
                    className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`}
                  />
                )}
                <p className="text-xs text-gray-400 mt-1">Status can only move forward (New → In Progress → Follow Up)</p>
              </div>
              <div>
                <label className={labelCls}>Currently Assigned To</label>
                <input
                  type="text" readOnly
                  value={`${assignedUserName}${assignedRole ? ` (${ROLE_LABELS[assignedRole] || assignedRole})` : ''}`}
                  className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`}
                />
              </div>
            </div>
          </div>

          {/* ── Dealer Details ── */}
          {lead.dealerInfo?.dealerName && (
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100">
                Dealer Details
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Dealer Name</label>
                  <input type="text" value={formData.dealerInfo.dealerName} readOnly
                    className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`} />
                </div>
                <div>
                  <label className={labelCls}>Dealer City</label>
                  <input
                    type="text"
                    value={formData.dealerInfo.dealerCity}
                    onChange={e => handleChange('dealerInfo', 'dealerCity', e.target.value)}
                    className={inputCls} placeholder="Enter dealer city"
                  />
                </div>
                <div>
                  <label className={labelCls}>Dealer Mobile</label>
                  <input type="tel" value={formData.dealerInfo.dealerNumber} readOnly
                    className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`} />
                </div>
                <div>
                  <label className={labelCls}>Dealer Email</label>
                  <input type="email" value={formData.dealerInfo.dealerEmail} readOnly
                    className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`} />
                </div>
              </div>
            </div>
          )}

          {/* ── Assignment History ── */}
          {lead.assignmentHistory?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100">
                Assignment History
              </h3>
              <div className="space-y-2">
                {lead.assignmentHistory.map((h, idx) => {
                  const fromName = h.fromUser?.firstName
                    ? `${h.fromUser.firstName} ${h.fromUser.lastName}`
                    : 'System';
                  const toName = h.toUser?.firstName
                    ? `${h.toUser.firstName} ${h.toUser.lastName}`
                    : 'Unknown';
                  const actionLabel = h.action === 'created' ? 'Assigned' : h.action === 'reopened' ? 'Reopened' : 'Forwarded';
                  const date = h.assignedAt ? new Date(h.assignedAt).toLocaleDateString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  }) : '';

                  return (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                          h.action === 'created' ? 'bg-green-500' : h.action === 'reopened' ? 'bg-orange-500' : 'bg-blue-500'
                        }`}>
                          {idx + 1}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800">
                          <span className="font-semibold">{actionLabel}</span>
                          {' by '}
                          <span className="font-medium">{fromName}</span>
                          {h.fromRole && <span className="text-gray-400"> ({ROLE_LABELS[h.fromRole] || h.fromRole})</span>}
                          {' → '}
                          <span className="font-medium">{toName}</span>
                          {h.toRole && <span className="text-gray-400"> ({ROLE_LABELS[h.toRole] || h.toRole})</span>}
                        </p>
                        {h.remark && <p className="text-xs text-gray-500 mt-0.5">{h.remark}</p>}
                        <p className="text-xs text-gray-400 mt-0.5">{date}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`/leads?expanded=${id}`)}
              className="btn-outline-secondary"
              disabled={updateLeadMutation.isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={updateLeadMutation.isLoading}
            >
              {updateLeadMutation.isLoading ? (
                <><Spinner size="sm" className="mr-2" />Saving...</>
              ) : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
